import { useEffect, useRef } from 'react';
import {
  useSessionAccountReady,
  useSessionMemberEmail,
} from 'hooks/useSessionVerification';
import { useSupportUserStore } from 'stores/supportUserStore';
import { storage, StorageKey } from 'utils/typedStorage';
import {
  isStoredEmailDiverged,
  setLocalStorageFromEmail,
} from 'utils/userInfoUtils';

import { useNeedsSupportSelection } from './useNeedsSupportSelection';

/**
 * Repairs a stored email that has diverged from the session member's email.
 * applyAuthData makes a single attempt when it detects the divergence and does not
 * retry a failed one, so this is the only remaining repair path. It no longer fires
 * unconditionally: that duplicated applyAuthData's own get_user_info lookup on every
 * app load.
 */
export const useSessionStorageSync = () => {
  const memberEmail = useSessionMemberEmail();
  const isAccountReady = useSessionAccountReady();
  const needsSupportSelection = useNeedsSupportSelection();
  const isSupportUser = useSupportUserStore((state) => state.isSupportUser);
  // The email the last request was issued for, so one diverging email is requested
  // once instead of on every re-render while that request is in flight.
  const requestedForEmail = useRef<string | null>(null);

  // Storage is read inside the effect only: every writer of the stored email reloads
  // or navigates away, so there is no in-document change for a render to observe.
  useEffect(() => {
    if (!isAccountReady || needsSupportSelection || !memberEmail) {
      return;
    }
    if (isSupportUser) {
      // Support logins pick a user to impersonate instead of storing their own.
      return;
    }
    if (!isStoredEmailDiverged(storage.get(StorageKey.Email), memberEmail)) {
      return;
    }
    if (requestedForEmail.current === memberEmail) {
      return;
    }
    requestedForEmail.current = memberEmail;
    void setLocalStorageFromEmail(memberEmail);
  }, [isAccountReady, memberEmail, needsSupportSelection, isSupportUser]);
};
